import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { KeyRound, AlertCircle, CheckCircle2, Mail, Timer } from 'lucide-react';
import { supabase, extractAccessTokenFromUrl } from '../lib/supabase'; // setSessionFromStoredToken 削除
import { cn } from '../utils/cn';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const _location = useLocation(); // アンダースコア追加で未使用警告回避
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [processingAuth, setProcessingAuth] = useState(true);
  // デバッグ情報は内部的に保持するが表示はしない
  const [_debugInfo, setDebugInfo] = useState<string[]>([]); // アンダースコア追加
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [sessionRestored, setSessionRestored] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);

  // デバッグ情報を追加する関数
  const addDebugInfo = (info: string) => {
    console.log(info); // コンソールにのみ出力
    setDebugInfo(prev => [...prev, `${new Date().toISOString().split('T')[1].split('.')[0]}: ${info}`]);
  };

  // Cooldown timer effect
  useEffect(() => {
    if (cooldownTime <= 0) return;

    const timer = setInterval(() => {
      setCooldownTime(time => Math.max(0, time - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownTime]);

  // Check if we're in reset mode (with type=recovery parameter)
  useEffect(() => {
    // Using an async IIFE to handle async operations
    (async () => {
      setProcessingAuth(true);
      setError(null);

      const fullUrl = window.location.href;
      const hasAccessToken = fullUrl.includes('access_token=');
      const hasTypeRecovery = fullUrl.includes('type=recovery');
      const hasTypeResetPW = fullUrl.includes('type=reset_password');

      // リセットフローの検出
      if (hasAccessToken || hasTypeRecovery || hasTypeResetPW) {
        setIsResetMode(true);
        try {
          // URL からセッション情報を復元
　　　　　const { data, error } = await supabase.auth.exchangeCodeForSession(window.location.href);
          
        if (error) {
          console.error('❌ セッション復元失敗:', error.message);
          setProcessingAuth(false);
          navigate('/login');
          return;
        }
          console.log('✅ セッション復元成功:', data);
          
          addDebugInfo('✅ セッション復元成功');
          setSessionRestored(true);
          addDebugInfo('✅ セッション復元完了');

          // access_token は data.session から取れる。なければ fallback
          setAccessToken(data?.session?.access_token ?? null);
          await supabase.auth.setSession({
            access_token: data?.session?.access_token!,
            refresh_token: data?.session?.refresh_token!,
          });
          addDebugInfo('🛠 setSession() を手動で実行しました');

          // Store token in localStorage for later use
          try {
            if (data?.session?.access_token) {
              localStorage.setItem('auth_access_token', data.session.access_token);
              localStorage.setItem('auth_refresh_token', data.session.refresh_token);
              localStorage.setItem('auth_token_stored_at', new Date().toISOString());
              addDebugInfo('Stored access token in localStorage');
            }
          } catch (e) {
            addDebugInfo(`Failed to store token: ${e instanceof Error ? e.message : String(e)}`);
          }
        } catch (err) {
          console.warn('⚠️ セッション復元エラー:', err);
          addDebugInfo(`❌ セッション復元失敗: ${err instanceof Error ? err.message : err}`);
          
          // トークンを直接URLから抽出して使用
          const token = extractAccessTokenFromUrl();
          if (token) {
            addDebugInfo(`🔑 URL から直接トークンを抽出しました: ${token.substring(0, 10)}...`);
            setAccessToken(token);
            
            try {
              // 手動でセッションを設定
              const { error } = await supabase.auth.setSession({
                access_token: token,
                refresh_token: token,
              });
              
              if (error) {
                addDebugInfo(`❌ 手動セッション設定エラー: ${error.message}`);
              } else {
                addDebugInfo('✅ 手動でセッションを設定しました');
                setSessionRestored(true);
              }
            } catch (e) {
              addDebugInfo(`❌ セッション設定例外: ${e instanceof Error ? e.message : String(e)}`);
            }
          }
        }
      } else {
        // 通常モード：リセットメール送信フォーム
        setIsResetMode(false);
      }

      setProcessingAuth(false);
    })();
  }, [navigate]);

  // Restore session from localStorage if available
  useEffect(() => {
    const access_token = localStorage.getItem('auth_access_token');
    const refresh_token = localStorage.getItem('auth_refresh_token') || access_token;
    if (access_token && refresh_token) {
      console.log('🔁 localStorage からセッションを再セットします');
      supabase.auth.setSession({
        access_token,
        refresh_token
      }).then(({ data, error }) => {
        console.log('🔁 再セット結果:', error ? `エラー: ${error.message}` : '成功');
        if (!error && data.session) {
          setAccessToken(data.session.access_token);
          setSessionRestored(true);
        }
      });
    } else {
      console.log('⚠️ トークンが localStorage に見つかりません');
    }
  }, []);

  const handleSendResetEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (cooldownTime > 0) {
      setError(`セキュリティのため、${cooldownTime}秒後に再試行してください。`);
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      addDebugInfo(`Sending password reset for: ${email}`);
      
      // Use the client API directly instead of the edge function
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email.trim(),
        {
          redirectTo: `${window.location.origin}/reset-password?type=recovery`
          // authOverride プロパティを削除（存在しないため）
        }
      );

      if (resetError) {
        if (resetError.message.includes('rate_limit')) {
          setCooldownTime(6); // Set cooldown timer for 6 seconds
          throw new Error('セキュリティのため、6秒後に再試行してください。');
        }
        throw resetError;
      }

      addDebugInfo('Password reset email sent successfully via client API');
      setMessage('パスワードリセットメールを送信しました。メールボックスを確認してください。');
      setSuccess(true);
      setCooldownTime(6); // Set cooldown timer after successful request
    } catch (err) {
      addDebugInfo(`Password reset request error: ${err instanceof Error ? err.message : String(err)}`);
      setError(err instanceof Error ? err.message : 'パスワードリセットメールの送信に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError('パスワードは8文字以上である必要があります');
      setLoading(false);
      return;
    }

    try {
      addDebugInfo('Resetting password');
      
      // If we have an access token, use it directly
      if (accessToken) {
        addDebugInfo(`Using direct token approach with token: ${accessToken.substring(0, 10)}...`);
        
        try {
          // Make a direct API call to Supabase Auth API
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/auth/v1/user`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
            },
            body: JSON.stringify({
              password: password
            })
          });
          
          const data = await response.json();
          
          if (!response.ok) {
            addDebugInfo(`Direct API call failed: ${JSON.stringify(data)}`);
            throw new Error(data.error || data.msg || 'パスワードの更新に失敗しました');
          }
          
          addDebugInfo('Password updated successfully via direct API call');
        } catch (directApiError) {
          addDebugInfo(`Direct API error: ${directApiError instanceof Error ? directApiError.message : String(directApiError)}`);
          
          // Try the SDK approach as fallback - authOverride削除
          try {
            addDebugInfo('Trying SDK approach');
            
            const { error: updateError } = await supabase.auth.updateUser(
              { password }
              // authOverride オプションを削除
            );
            
            if (updateError) {
              addDebugInfo(`SDK update failed: ${updateError.message}`);
              throw updateError;
            }
            
            addDebugInfo('Password updated successfully via SDK');
          } catch (sdkError) {
            addDebugInfo(`SDK error: ${sdkError instanceof Error ? sdkError.message : String(sdkError)}`);
            
            // Try setting session first, then update
            try {
              addDebugInfo('Trying to set session first, then update');
              
              const { error: sessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: accessToken
              });
              
              if (sessionError) {
                addDebugInfo(`Session setting failed: ${sessionError.message}`);
                throw sessionError;
              }
              
              addDebugInfo('Session set successfully, now updating password');
              
              const { error: finalUpdateError } = await supabase.auth.updateUser({
                password
              });
              
              if (finalUpdateError) {
                addDebugInfo(`Final update failed: ${finalUpdateError.message}`);
                throw finalUpdateError;
              }
              
              addDebugInfo('Password updated successfully after setting session');
            } catch (finalError) {
              addDebugInfo(`Final approach failed: ${finalError instanceof Error ? finalError.message : String(finalError)}`);
              throw finalError;
            }
          }
        }
      } else {
        // No access token, try normal update
        addDebugInfo('No access token available, trying normal update');
        
        const { error: updateError } = await supabase.auth.updateUser({
          password
        });
        
        if (updateError) {
          addDebugInfo(`Normal update failed: ${updateError.message}`);
          throw updateError;
        }
        
        addDebugInfo('Password updated successfully via normal update');
      }

      addDebugInfo('Password reset successful');
      setSuccess(true);
      
      // Clear any stored recovery URL
      try {
        localStorage.removeItem('auth_recovery_url');
        localStorage.removeItem('auth_recovery_time');
        localStorage.removeItem('auth_recovery_event');
        localStorage.removeItem('auth_access_token');
        localStorage.removeItem('auth_refresh_token');
        localStorage.removeItem('auth_on_reset_page');
        localStorage.removeItem('auth_redirect_time');
        localStorage.removeItem('auth_token_stored_at');
      } catch (e) {
        console.error('Failed to clear auth recovery data:', e);
      }
      
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      addDebugInfo(`Password update error: ${err instanceof Error ? err.message : String(err)}`);
      setError(err instanceof Error ? err.message : 'パスワードの更新に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  if (processingAuth) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-xl p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                認証情報を処理中...
              </h2>
              <p className="text-gray-600">
                しばらくお待ちください
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!sessionRestored && isResetMode) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-xl p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                セッション復元中です
              </h2>
              <p className="text-gray-600 mb-4">
                しばらくお待ちください
              </p>
              <button
                onClick={() => setSessionRestored(true)}
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                続行する
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-xl p-8">
            <div className="text-center">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {isResetMode ? 'パスワードが更新されました' : 'パスワードリセットのメールを送信しました'}
              </h2>
              <p className="text-gray-600 mb-4">
                {isResetMode 
                  ? 'ログインページに移動します...' 
                  : message || 'メールの指示に従ってパスワードを再設定してください。'}
              </p>
              <button
                onClick={() => navigate('/login')}
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                ログインページに戻る
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <KeyRound className="h-12 w-12 text-primary-600" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">
              {isResetMode ? 'パスワード再設定' : 'パスワードリセット'}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {isResetMode 
                ? '新しいパスワードを入力してください' 
                : '登録したメールアドレスを入力してください'}
            </p>
          </div>

          {isResetMode ? (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  新しいパスワード
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                  placeholder="••••••••"
                  minLength={8}
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  パスワード（確認）
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                  placeholder="••••••••"
                  minLength={8}
                />
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">{error}</h3>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={cn(
                  "w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500",
                  loading && "opacity-70 cursor-not-allowed"
                )}
              >
                {loading ? '処理中...' : 'パスワードを更新する'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-primary-600 hover:text-primary-500"
                >
                  ログインページに戻る
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSendResetEmail} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  メールアドレス
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="example@domain.com"
                />
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">{error}</h3>
                    </div>
                  </div>
                </div>
              )}

              {cooldownTime > 0 && (
                <div className="flex items-center justify-center text-sm text-gray-600">
                  <Timer className="w-4 h-4 mr-1" />
                  <span>{cooldownTime}秒後に再試行できます</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || cooldownTime > 0}
                className={cn(
                  "w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500",
                  (loading || cooldownTime > 0) && "opacity-70 cursor-not-allowed"
                )}
              >
                {loading ? 'メール送信中...' : 'リセットメールを送信'}
              </button>

              <div className="text-center mt-4">
                <div className="text-sm text-gray-600 mb-2">
                  <Mail className="inline-block w-4 h-4 mr-1" />
                  メールが届かない場合は、迷惑メールフォルダを確認してください
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-primary-600 hover:text-primary-500"
                >
                  ログインページに戻る
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;